import { useMemo } from 'react';
import { FieldPath, FieldValues, useWatch } from 'react-hook-form';
import {
  decodeClassifierMappingOptionValue,
  encodeClassifierMappingOptionValue,
} from '../../../classifier-mapping';
import { useInfiniteSearchClassifierMappingsQuery } from '../../../classifier-mapping/query/useInfiniteSearchClassifierMappingsQuery';
import {
  createInfiniteSearchSelect,
  FormSelectProps,
} from '../../../common/form';
import { decodeAlgorithmSourceDimensionOptionValue } from '../sources/utils';

export interface AlgorithmMappingClassifierMappingSelectProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name],
> extends FormSelectProps<FormData, Name, Value> {
  areDbSources: boolean;
}

const ClassifierMappingInfiniteFormSelect = createInfiniteSearchSelect(
  (term, options) =>
    useInfiniteSearchClassifierMappingsQuery(term, {
      ...options,
      getOptionFromSearchResult: ({ id, code, name }) => ({
        value: encodeClassifierMappingOptionValue({ id, code, name }),
        label: `${code} - ${name}`,
      }),
    }),
  true,
);

export const AlgorithmMappingClassifierMappingFormSelect = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name],
>(
  props: AlgorithmMappingClassifierMappingSelectProps<FormData, Name, Value>,
) => {
  const { name, areDbSources, ...rest } = props;
  const baseName = name.substring(0, name.lastIndexOf('.'));
  const classifierMapping = useWatch({
    name,
  }) as string | null;

  const sourceDimension = useWatch({
    name: `${baseName}.sourceDimension`,
  }) as string | null;

  const { dimensionCode, classifierId } = sourceDimension
    ? decodeAlgorithmSourceDimensionOptionValue(sourceDimension)
    : {};

  const { classifierMappingCode } = classifierMapping
    ? decodeClassifierMappingOptionValue(classifierMapping)
    : {};

  const searchOptions = useMemo(
    () => ({
      classifierIdFrom: classifierId,
      classifierCodeFrom:
        areDbSources || classifierId ? undefined : dimensionCode,
      selectedMapping: classifierMappingCode,
    }),
    [areDbSources, classifierId, dimensionCode, classifierMappingCode],
  );

  return (
    <ClassifierMappingInfiniteFormSelect
      {...rest}
      styles={{
        popup: { root: { width: '350px' } },
      }}
      searchOptions={searchOptions}
      name={name}
    />
  );
};
